import type { TFunction } from 'i18next'

import { WhatsNewEntry } from './whatsNewTypes'

export const getWhatsNewEntries = (t: TFunction): WhatsNewEntry[] => [
    {
        id: '2026-11-04-schedule-recurring-analysis-with-agents',
        date: '2026-11-04',
        category: 'newFeature',
        featureFlag: 'scheduledAgents',
        availability: { all: ['agents'] },
        missingPermissionBehavior: 'hide',
        title: t(
            'navigation:TopNav.WhatsNew.entries.scheduleRecurringAnalysisWithAgents.title',
            'Schedule recurring analysis with Agents'
        ),
        content: [
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.scheduleRecurringAnalysisWithAgents.items.buildAgent',
                        'Build an agent by describing the analysis you need or by selecting a template'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.scheduleRecurringAnalysisWithAgents.items.scheduleRoutines',
                        'Run analysis daily, weekly, or monthly and receive reports or Excel files by email'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.scheduleRecurringAnalysisWithAgents.items.setAlerts',
                        'Set alerts for agents to only notify you when a metric crosses your chosen threshold'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.scheduleRecurringAnalysisWithAgents.items.findReports',
                        'Find and search every report and file your agents create or teammates share with you in Artifacts'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.scheduleRecurringAnalysisWithAgents.items.shareAgentsAndReports',
                        'Share agents and their reports with teammates'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-11-02-create-and-apply-labels-with-ask-cobalt',
        date: '2026-11-02',
        category: 'newFeature',
        availability: { all: ['manageLabels'] },
        missingPermissionBehavior: 'hide',
        title: t(
            'navigation:TopNav.WhatsNew.entries.createAndApplyLabelsWithAskCobalt.title',
            'Create and apply labels with Ask Cobalt'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.createAndApplyLabelsWithAskCobalt.description',
                    '<aiChat>Ask Cobalt</aiChat> can now create, update, and apply labels for you, making it faster to organize your work using plain language.'
                ),
                links: {
                    aiChat: { href: '/ai/chat' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.createAndApplyLabelsWithAskCobalt.items.createLabels',
                        'Create organization labels with a name, description, and color'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.createAndApplyLabelsWithAskCobalt.items.editLabels',
                        'Rename labels or change their description or color while keeping them on everything already labeled'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.createAndApplyLabelsWithAskCobalt.items.applyAndRemoveLabels',
                        'Apply or remove labels on profiles, portfolios, campaigns, products, Share of Voice keywords, and DSP orders and line items'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-10-29-choose-which-dsp-advertisers-to-include',
        date: '2026-10-29',
        category: 'newFeature',
        availability: { all: ['dspIntegration'] },
        missingPermissionBehavior: 'hide',
        title: t(
            'navigation:TopNav.WhatsNew.entries.chooseWhichDspAdvertisersToInclude.title',
            'Choose which DSP advertisers to include'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.chooseWhichDspAdvertisersToInclude.description',
                    'Organization admins can now choose which DSP advertisers each Amazon Ads integration brings into Cobalt from <integrations>Settings → Integrations</integrations>.'
                ),
                links: {
                    integrations: { href: '/settings/amazon-integrations' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.chooseWhichDspAdvertisersToInclude.items.selectAdvertisers',
                        'Select only the DSP advertisers that belong to your organization'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.chooseWhichDspAdvertisersToInclude.items.stopSyncing',
                        'Stop syncing excluded advertisers and remove their data from your DSP views, dashboards, and Ask Cobalt'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.chooseWhichDspAdvertisersToInclude.items.controlNewAdvertisers',
                        'Choose whether newly discovered DSP advertisers are included automatically'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.chooseWhichDspAdvertisersToInclude.items.restoreAdvertisers',
                        'Include an advertiser again at any time to restore it without deleting data'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-10-29-analyze-seller-central-sales-by-sku',
        date: '2026-10-29',
        category: 'newFeature',
        availability: { all: ['sellerCentral'] },
        title: t(
            'navigation:TopNav.WhatsNew.entries.analyzeSellerCentralSalesBySku.title',
            'Analyze Seller Central sales by SKU'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.analyzeSellerCentralSalesBySku.description',
                    'Break down Seller Central sales by SKU in <dashboards>Dashboards</dashboards> and <aiChat>Ask Cobalt</aiChat> to see how each SKU performs alongside its ASIN.'
                ),
                links: {
                    dashboards: { href: '/dashboards' },
                    aiChat: { href: '/ai/chat' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeSellerCentralSalesBySku.items.groupDashboardCharts',
                        'Group dashboard tables, time series, and category charts by SKU to compare sales, units, order items, and advertising metrics'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeSellerCentralSalesBySku.items.askSkuQuestions',
                        'Ask Cobalt or the MCP for sales by SKU, or compare FBA and FBM sales for a product'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeSellerCentralSalesBySku.items.reviewTwoYears',
                        'Review up to two years of SKU sales history for every connected Seller Central account'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeSellerCentralSalesBySku.items.useProductForTraffic',
                        'Continue grouping by product for traffic metrics such as sessions, page views, Buy Box, and conversion, which Amazon does not report by SKU'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeSellerCentralSalesBySku.items.distinguishAdvertisedSku',
                        'Use SKU for every SKU that sold, or Advertised SKU for SKUs with Sponsored Products or Sponsored Display activity'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeSellerCentralSalesBySku.items.sellerCentralOnly',
                        'Analyze SKU-level performance for Seller Central accounts; Vendor Central does not report at SKU level'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-10-23-seller-compliance-now-works-for-japan',
        date: '2026-10-23',
        category: 'improvement',
        availability: { all: ['sellerCompliance'] },
        title: t(
            'navigation:TopNav.WhatsNew.entries.sellerComplianceNowWorksForJapan.title',
            'Seller Compliance now works for Japan'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.sellerComplianceNowWorksForJapan.description',
                    '<sellerCompliance>Seller Compliance</sellerCompliance> now shows who is winning your Buy Box in Japan and the estimated revenue you may be losing.'
                ),
                links: {
                    sellerCompliance: { href: '/distribution-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.sellerComplianceNowWorksForJapan.items.identifyBuyBoxWinners',
                        'Identify the sellers winning the Buy Box for your products in Japan'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.sellerComplianceNowWorksForJapan.items.measureLostRevenue',
                        'See estimated Buy Box share and Lost Revenue based on the last 30 days of Buy Box checks'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.sellerComplianceNowWorksForJapan.items.reviewEstimateMethod',
                        'Hover over the Estimated label to see how Buy Box share is calculated'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-10-19-more-accurate-amazon-performance-data',
        date: '2026-10-19',
        category: 'dataUpdate',
        title: t(
            'navigation:TopNav.WhatsNew.entries.moreAccurateAmazonPerformanceData.title',
            'More accurate Amazon performance data'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.moreAccurateAmazonPerformanceData.description',
                    'Your Amazon Ads, Seller Central, and Vendor Central performance data in <dashboards>Dashboards</dashboards> now comes through an upgraded pipeline for more complete, accurate reporting from October 19 onward.'
                ),
                links: {
                    dashboards: { href: '/dashboards' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.moreAccurateAmazonPerformanceData.items.reviewDspPerformance',
                        'Review more complete Amazon DSP performance data across audience and inventory reporting'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.moreAccurateAmazonPerformanceData.items.relyOnAccurateReporting',
                        'Rely on more accurate sales reporting for Sponsored Products, Sponsored Brands, and Sponsored Display'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.moreAccurateAmazonPerformanceData.items.useCentralReports',
                        'Track Seller and Vendor Central sales, traffic, and inventory with more reliable reporting'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-10-14-use-your-labels-in-ask-cobalt',
        date: '2026-10-14',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.useYourLabelsInAskCobalt.title',
            'Use your labels in Ask Cobalt'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.useYourLabelsInAskCobalt.description',
                    "<aiChat>Ask Cobalt</aiChat> can now use your organization's labels to answer questions about the campaigns, products, keywords, and Amazon DSP activity you organize in Cobalt."
                ),
                links: {
                    aiChat: { href: '/ai/chat' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.useYourLabelsInAskCobalt.items.findLabels',
                        'Find labels by name and see where they are used across your organization'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.useYourLabelsInAskCobalt.items.filterByLabel',
                        'Filter supported Ads, Amazon DSP, Share of Voice, Seller Central, Vendor Central, and Inventory data by label'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.useYourLabelsInAskCobalt.items.splitByLabel',
                        'Split performance results into one row per label, with unlabelled data and overlapping groups clearly identified'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.useYourLabelsInAskCobalt.items.catchLabelMismatch',
                        'Get a clear explanation when a label belongs to a different organization or entity type'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-10-08-find-reports-faster-in-artifacts',
        date: '2026-10-08',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.findReportsFasterInArtifacts.title',
            'Find reports faster in Artifacts'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.findReportsFasterInArtifacts.description',
                    'The redesigned <artifacts>Artifacts</artifacts> page makes it easier to find, preview, and revisit every report and file Cobalt AI has created for you.'
                ),
                links: {
                    artifacts: { href: '/ai/artifacts' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.findReportsFasterInArtifacts.items.searchReports',
                        'Search report names and contents, including the brands, products, and sellers they mention'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.findReportsFasterInArtifacts.items.filterAndSort',
                        'Filter by type, creator, or date, and sort by newest, oldest, or name'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.findReportsFasterInArtifacts.items.previewReports',
                        "Preview each report's headline, key figure, and top findings from its card"
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.findReportsFasterInArtifacts.items.chooseAView',
                        'Switch between card and table views, browse by date, and return to the same search, filters, and page after opening a report'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-10-06-create-pdf-reports-with-ask-cobalt',
        date: '2026-10-06',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.createPdfReportsWithAskCobalt.title',
            'Create PDF reports with Ask Cobalt'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.createPdfReportsWithAskCobalt.description',
                    'Ask <aiChat>Ask Cobalt</aiChat> to create a polished, downloadable PDF report directly from your conversation.'
                ),
                links: {
                    aiChat: { href: '/ai/chat' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.createPdfReportsWithAskCobalt.items.generateReports',
                        'Generate a downloadable PDF report by asking for one in your conversation'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.createPdfReportsWithAskCobalt.items.createOnePager',
                        'Request a one-pager to keep the report on a single page'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-10-05-start-with-ask-cobalt-on-the-homepage',
        date: '2026-10-05',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.startWithAskCobaltOnTheHomepage.title',
            'Start with Ask Cobalt on the homepage'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.startWithAskCobaltOnTheHomepage.description',
                    'Ask Cobalt is now at the top of your <homepage>homepage</homepage>, so you can start a question as soon as you sign in.'
                ),
                links: {
                    homepage: { href: '/' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.startWithAskCobaltOnTheHomepage.items.askAQuestion',
                        'Ask Cobalt a question directly from your homepage'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.startWithAskCobaltOnTheHomepage.items.startWithoutOpeningChat',
                        'Start exploring without navigating to the full Ask Cobalt chat page first'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-08-21-set-currency-for-each-dashboard-tab',
        date: '2026-08-21',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.setCurrencyForEachDashboardTab.title',
            'Set currency for each dashboard tab'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.setCurrencyForEachDashboardTab.description',
                    'Choose a currency for each tab in <dashboards>Dashboards</dashboards>, so every widget on that tab displays and exports in the right currency.'
                ),
                links: {
                    dashboards: { href: '/dashboards' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.setCurrencyForEachDashboardTab.items.manageTabs',
                        'Set a tab currency from Manage Tabs'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.setCurrencyForEachDashboardTab.items.overrideDashboardCurrency',
                        'Override the dashboard or global currency for individual tabs'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.setCurrencyForEachDashboardTab.items.keepCurrencyEverywhere',
                        'Keep the tab currency in CSV exports, shared and read-only dashboards, and one-off or scheduled PDFs'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-08-31-schedule-executive-homepage-emails',
        date: '2026-08-31',
        category: 'newFeature',
        availability: { all: ['executiveHomepage'] },
        title: t(
            'navigation:TopNav.WhatsNew.entries.scheduleExecutiveHomepageEmails.title',
            'Schedule Executive Homepage emails'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.scheduleExecutiveHomepageEmails.description',
                    'Automatically share a PDF of your <executiveHomepage>Executive Homepage</executiveHomepage> by email on the schedule that works for your team.'
                ),
                links: {
                    executiveHomepage: { href: '/' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.scheduleExecutiveHomepageEmails.items.chooseFrequency',
                        'Choose daily, weekly, monthly, or quarterly delivery'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.scheduleExecutiveHomepageEmails.items.sendToAnyone',
                        'Send reports to any email address, including recipients outside your organization'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.scheduleExecutiveHomepageEmails.items.testEmail',
                        'Send a test email before scheduling to preview what recipients will receive'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-09-30-visualize-answers-with-charts-in-ask-cobalt',
        date: '2026-09-30',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.visualizeAnswersWithChartsInAskCobalt.title',
            'Visualize answers with charts in Ask Cobalt'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.visualizeAnswersWithChartsInAskCobalt.description',
                    '<aiChat>Ask Cobalt</aiChat> can now turn your data into inline charts when you request one or when a visual makes the answer clearer.'
                ),
                links: {
                    aiChat: { href: '/ai/chat' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.visualizeAnswersWithChartsInAskCobalt.items.chartTypes',
                        'Explore your data in column, bar, and time-series charts'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.visualizeAnswersWithChartsInAskCobalt.items.askForAChart',
                        'Ask for a chart directly or let Ask Cobalt choose one when it fits your question'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.visualizeAnswersWithChartsInAskCobalt.items.viewChartsAnywhere',
                        'View charts in the side panel, full chat page, and past conversations'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-09-15-export-ask-cobalt-data-to-excel',
        date: '2026-09-15',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.exportAskCobaltDataToExcel.title',
            'Export Ask Cobalt data to Excel'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.exportAskCobaltDataToExcel.description',
                    'Ask <aiChat>Ask Cobalt</aiChat> to turn the data it pulls into an Excel spreadsheet, then download it directly from your conversation.'
                ),
                links: {
                    aiChat: { href: '/ai/chat' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.exportAskCobaltDataToExcel.items.exportAnyData',
                        'Create a spreadsheet from any data Ask Cobalt can pull'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.exportAskCobaltDataToExcel.items.useMultipleSheets',
                        'Organize related results across multiple sheets in one workbook'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.exportAskCobaltDataToExcel.items.formatData',
                        'Get basic formatting for headers, currency, numbers, dates, and percentages'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-09-24-analyze-competitive-offers-in-the-mcp',
        date: '2026-09-24',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.analyzeCompetitiveOffersInTheMcp.title',
            'Analyze competitive offers in the MCP'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.analyzeCompetitiveOffersInTheMcp.description',
                    'Rebuilt Competitive Offers tools in the Jungle Scout MCP let you investigate buy box competition for any ASIN or brand, without a connected Seller or Vendor Central account.'
                ),
                links: {},
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeCompetitiveOffersInTheMcp.items.compareOffers',
                        'Compare every new-condition offer by price, buy box share, and fulfillment type, with Amazon offers clearly identified'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeCompetitiveOffersInTheMcp.items.diagnoseSuppression',
                        'When Amazon provides its competitive price threshold, track suppression over time and see the price cut needed to compete for the buy box'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeCompetitiveOffersInTheMcp.items.followSellers',
                        "Follow one seller's pricing and buy box share across an entire catalog, with no 30-ASIN limit"
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeCompetitiveOffersInTheMcp.items.contactCsm',
                        '<strong>To learn more about the MCP or get access, contact your CSM</strong>'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-09-18-control-which-brands-count-as-yours',
        date: '2026-09-18',
        category: 'improvement',
        availability: { any: ['sellerCentral', 'vendorCentral'] },
        title: t(
            'navigation:TopNav.WhatsNew.entries.controlWhichBrandsCountAsYours.title',
            'Control which brands count as yours'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.controlWhichBrandsCountAsYours.description',
                    'Review the brands discovered through your connected Seller Central or Vendor Central account in <brandSettings>Settings</brandSettings>, and choose which ones Cobalt treats as yours in Market Trends.'
                ),
                links: {
                    brandSettings: { href: '/settings/brands' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.controlWhichBrandsCountAsYours.items.excludeBrands',
                        'Organization admins can switch off brands that should not count as yours'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.controlWhichBrandsCountAsYours.items.updateMarketTrends',
                        'Keep your Yours labels, brand filters, and combined market share accurate across Market Trends'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-09-14-analyze-seller-compliance-in-the-mcp',
        date: '2026-09-14',
        availability: { any: ['sellerCentral', 'vendorCentral'] },
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.analyzeSellerComplianceInTheMcp.title',
            'Analyze Seller Compliance in the MCP'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.analyzeSellerComplianceInTheMcp.description',
                    'The Cobalt MCP can now analyze the same data that powers <sellerCompliance>Seller Compliance</sellerCompliance>, using your connected Seller Central or Vendor Central account.'
                ),
                links: {
                    sellerCompliance: { href: '/distribution-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeSellerComplianceInTheMcp.items.measureRevenueAndBuyBoxImpact',
                        'Measure potential lost revenue, Buy Box win share, and third-party sales share by brand, product, seller, or marketplace'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeSellerComplianceInTheMcp.items.findUnauthorizedSellers',
                        'Find the biggest unauthorized sellers across your catalog and see their registered business names'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeSellerComplianceInTheMcp.items.trackSuppressedBuyBoxes',
                        'Track suppressed Buy Boxes across brands, products, sellers, and marketplaces'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeSellerComplianceInTheMcp.items.contactCsm',
                        'To learn more about the MCP or get access, contact your CSM'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-09-02-analyze-products-brands-and-sellers-directly',
        date: '2026-09-02',
        category: 'newFeature',
        featureFlag: 'marketTrendsStandalonePages',
        title: t(
            'navigation:TopNav.WhatsNew.entries.analyzeProductsBrandsAndSellersDirectly.title',
            'Analyze products, brands, and sellers directly'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.analyzeProductsBrandsAndSellersDirectly.description',
                    'Explore the new <products>Products</products>, <brands>Brands</brands>, and <sellers>Sellers</sellers> pages to analyze performance across Amazon without building a Market Analysis segment first.'
                ),
                links: {
                    products: { href: '/market-analysis/products' },
                    brands: { href: '/market-analysis/brands' },
                    sellers: { href: '/market-analysis/sellers' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeProductsBrandsAndSellersDirectly.items.searchAndAnalyze',
                        'Search for any product, brand, or seller and see its total performance across Amazon'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeProductsBrandsAndSellersDirectly.items.filterByPerformance',
                        'Filter tables by performance to find the products, brands, or sellers that meet your criteria'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeProductsBrandsAndSellersDirectly.items.compareCompetitors',
                        'Discover and compare suggested competitors for products and brands'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-08-20-explore-customer-reviews-in-market-analysis',
        date: '2026-08-20',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.exploreCustomerReviewsInMarketAnalysis.title',
            'Explore customer reviews in Market Analysis'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.exploreCustomerReviewsInMarketAnalysis.description',
                    'The new Reviews tab in <marketAnalysis>Market Analysis</marketAnalysis> turns customer feedback into clear insights for your segment.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.exploreCustomerReviewsInMarketAnalysis.items.topPraiseAndComplaints',
                        'Quickly see the top praise, complaints, and summarized takeaways from customer reviews'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.exploreCustomerReviewsInMarketAnalysis.items.compareBrands',
                        'Compare brands to see where each one stands out in customer feedback'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.exploreCustomerReviewsInMarketAnalysis.items.drillIntoReviews',
                        'Drill into specific review topics, brands, and products to understand what customers are saying'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-08-19-run-analysis-workflows-in-ask-cobalt',
        date: '2026-08-19',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.runAnalysisWorkflowsInAskCobalt.title',
            'Run analysis workflows in Ask Cobalt'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.runAnalysisWorkflowsInAskCobalt.description',
                    'Launch in-depth reports from <aiChat>Ask Cobalt</aiChat>, then keep exploring the results in the same conversation.'
                ),
                links: {
                    aiChat: { href: '/ai/chat' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.runAnalysisWorkflowsInAskCobalt.items.chooseAReport',
                        'Choose Category Analysis, Segment Analysis, or Brand Benchmarking under Generate Report'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.runAnalysisWorkflowsInAskCobalt.items.startFromAnywhere',
                        'Start from the Ask Cobalt panel on any page or from the full chat page'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.runAnalysisWorkflowsInAskCobalt.items.continueExploring',
                        'Find completed reports in Artifacts and ask follow-up questions to dig deeper into the findings'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-08-19-export-and-schedule-executive-homepage-pdfs',
        date: '2026-08-19',
        category: 'newFeature',
        availability: { all: ['executiveHomepage'] },
        title: t(
            'navigation:TopNav.WhatsNew.entries.exportAndScheduleExecutiveHomepagePdfs.title',
            'Export and schedule Executive Homepage PDFs'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.exportAndScheduleExecutiveHomepagePdfs.description',
                    'Share your <executiveHomepage>Executive Homepage</executiveHomepage> as a PDF, directly from Overview actions.'
                ),
                links: {
                    executiveHomepage: { href: '/' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.exportAndScheduleExecutiveHomepagePdfs.items.exportPdf',
                        'Export the current overview to PDF in the app'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.exportAndScheduleExecutiveHomepagePdfs.items.emailPdf',
                        'Email the PDF now or on a daily or weekly schedule'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.exportAndScheduleExecutiveHomepagePdfs.items.matchOverview',
                        'Keep the filters, metrics, and breakdown selected on your Executive Homepage in the PDF'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-08-19-filter-dsp-widgets-by-targeting-and-supply-source',
        date: '2026-08-19',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.filterDspWidgetsByTargetingAndSupplySource.title',
            'Filter DSP widgets by targeting and supply source'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.filterDspWidgetsByTargetingAndSupplySource.description',
                    'Two new filters on <dashboards>DSP dashboard widgets</dashboards>, plus a fix to how orders are named in exports.'
                ),
                links: {
                    dashboards: { href: '/dashboards' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.filterDspWidgetsByTargetingAndSupplySource.items.targetingMethodFilter',
                        'Filter DSP Audience widgets to just targeted or just untargeted with the new Targeting Method filter'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.filterDspWidgetsByTargetingAndSupplySource.items.supplySourceFilter',
                        'Filter DSP Inventory widgets by Supply Source directly, without relying on line item labels'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.filterDspWidgetsByTargetingAndSupplySource.items.orderNamesInExports',
                        'CSV exports from DSP widgets now use order names instead of order IDs'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-08-18-see-brand-logos-in-market-analysis',
        date: '2026-08-18',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.seeBrandLogosInMarketAnalysis.title',
            'See brand logos in Market Analysis'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.seeBrandLogosInMarketAnalysis.description',
                    '<marketAnalysis>Market Analysis</marketAnalysis> segments in the US marketplace now show brand logos where available, making it faster to tell brands apart as you scan.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.seeBrandLogosInMarketAnalysis.items.logosAcrossUsSegments',
                        'Brand logos appear across Market Analysis views for US segments'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-08-14-dashboard-pdfs-now-break-by-tab',
        date: '2026-08-14',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.dashboardPdfsNowBreakByTab.title',
            'Dashboard PDFs now break by tab'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.dashboardPdfsNowBreakByTab.description',
                    '<dashboards>Dashboard PDF exports</dashboards> now put each dashboard tab on its own page, so long dashboards open faster and are easier to navigate.'
                ),
                links: {
                    dashboards: { href: '/dashboards' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.dashboardPdfsNowBreakByTab.items.pagePerTab',
                        'Each tab becomes its own page, preserving the structure you built the dashboard with'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.dashboardPdfsNowBreakByTab.items.noSplitWidgets',
                        'Widgets still never split across a page break'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.dashboardPdfsNowBreakByTab.items.smootherRendering',
                        'Large dashboards render more smoothly in Chrome and other PDF viewers'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-08-14-three-upgrades-to-the-keywords-tab',
        date: '2026-08-14',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.threeUpgradesToTheKeywordsTab.title',
            'Three upgrades to the Keywords tab'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.threeUpgradesToTheKeywordsTab.description',
                    'New ways to read keyword competition and sponsored visibility in the <marketAnalysis>Market Analysis</marketAnalysis> Keywords tab.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.threeUpgradesToTheKeywordsTab.items.sponsoredSovByAdType',
                        'Hover a sponsored bar in Brand Visibility to split it into Sponsored Product, Headline, and Video'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.threeUpgradesToTheKeywordsTab.items.easeToRankScore',
                        'A new Ease-to-Rank score (0 to 100) shows how easy a keyword is to rank for, based on its competition'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.threeUpgradesToTheKeywordsTab.items.limitToSegmentProducts',
                        'Limit Share of Voice results to just the products in your segment, instead of every product that appears on the keyword'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-08-14-ask-about-category-traffic-and-conversion',
        date: '2026-08-14',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.askAboutCategoryTrafficAndConversion.title',
            'Ask about category traffic and conversion'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.askAboutCategoryTrafficAndConversion.description',
                    '<aiChat>Cobalt AI Chat</aiChat> and the MCP can now answer questions from Category Insights, the same data behind the <categoryPerformance>Category Performance</categoryPerformance> page.'
                ),
                links: {
                    aiChat: { href: '/ai/chat' },
                    categoryPerformance: { href: '/category-performance' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.askAboutCategoryTrafficAndConversion.items.categoryPerformance',
                        'Ask how a category or subcategory is performing on revenue, units, and average selling price'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.askAboutCategoryTrafficAndConversion.items.shopperBehavior',
                        'Go deeper on shopper behavior: searches, glance views, click-through rate, conversion rate, and returns'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.askAboutCategoryTrafficAndConversion.items.contactCsm',
                        'To learn more about the MCP or get access, contact your CSM'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-08-13-manage-ad-groups-in-campaign-manager',
        date: '2026-08-13',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.manageAdGroupsInCampaignManager.title',
            'Manage ad groups in Campaign Manager'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.manageAdGroupsInCampaignManager.description',
                    '<campaigns>Campaign Manager</campaigns> now has an <adGroups>Ad Groups tab</adGroups>, so bid and state changes can happen at the ad group level instead of stopping at the campaign.'
                ),
                links: {
                    campaigns: { href: '/campaigns' },
                    adGroups: { href: '/campaigns?view=adGroups' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.manageAdGroupsInCampaignManager.items.inlineAndBulkEdits',
                        'Edit ad group state and default bid inline, or select multiple rows and change them in bulk'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.manageAdGroupsInCampaignManager.items.filterSortExport',
                        'Filter by Ad Group State and Campaign State, sort by any column, and export the table'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-08-13-filter-a-dashboard-tab-by-dsp-labels',
        date: '2026-08-13',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.filterADashboardTabByDspLabels.title',
            'Filter a dashboard tab by DSP labels'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.filterADashboardTabByDspLabels.description',
                    'DSP Order Labels and DSP Line Item Labels are now tab-level filters on <dashboards>dashboards</dashboards>, so one label scopes every widget on the tab at once.'
                ),
                links: {
                    dashboards: { href: '/dashboards' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.filterADashboardTabByDspLabels.items.scopeEntireTab',
                        'Scope an entire dashboard tab to a funnel stage, audience strategy, or tactic in a single filter'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.filterADashboardTabByDspLabels.items.savePerWorkstream',
                        'Save the dashboard to keep the filter in place, so you can keep one dashboard per workstream'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.filterADashboardTabByDspLabels.items.joinsExistingLabelFilters',
                        'Joins Tracked Keyword, Product, and Campaign labels as tab filter options'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-08-12-know-how-long-your-1p-inventory-will-last',
        date: '2026-08-12',
        category: 'dataUpdate',
        title: t(
            'navigation:TopNav.WhatsNew.entries.knowHowLongYour1pInventoryWillLast.title',
            'Know how long your 1P inventory will last'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.knowHowLongYour1pInventoryWillLast.description',
                    'Two new Weeks of Coverage metrics for Vendor Central on <dashboards>dashboards</dashboards> answer how many weeks your current inventory will last at your recent sales pace.'
                ),
                links: {
                    dashboards: { href: '/dashboards' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.knowHowLongYour1pInventoryWillLast.items.fourWeekWindow',
                        'Weeks of Coverage · 4-Week (T4W) tracks recent pace, surfacing near-term stockout and overstock risk'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.knowHowLongYour1pInventoryWillLast.items.sixteenWeekWindow',
                        'Weeks of Coverage · 16-Week (T16W) smooths week-to-week noise and seasonality for longer-range planning'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.knowHowLongYour1pInventoryWillLast.items.liveDashboardColumns',
                        'Available as live dashboard columns, replacing the spreadsheet many brands rebuild by hand each week'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-08-10-export-the-full-performance-breakdown-table',
        date: '2026-08-10',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.exportTheFullPerformanceBreakdownTable.title',
            'Export the full Performance Breakdown table'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.exportTheFullPerformanceBreakdownTable.description',
                    'You can now download the complete Performance Breakdown table from <sellerCompliance>Seller Compliance</sellerCompliance>, for both the Brand and the Product breakdown.'
                ),
                links: {
                    sellerCompliance: { href: '/distribution-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.exportTheFullPerformanceBreakdownTable.items.howToDownload',
                        'Click View All under Performance Breakdown, then the download icon next to the date range'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.exportTheFullPerformanceBreakdownTable.items.matchesFiltersAndSort',
                        'The file matches your filters and sort order, and includes every row, not just what is on screen'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.exportTheFullPerformanceBreakdownTable.items.findUnderExports',
                        'Find the file under Exports in the top nav when it is ready'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-31-see-the-keywords-driving-any-market',
        date: '2026-07-31',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.seeTheKeywordsDrivingAnyMarket.title',
            'See the keywords driving any market'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.seeTheKeywordsDrivingAnyMarket.description',
                    "The new Keywords tab in <marketAnalysis>Market Analysis</marketAnalysis> connects a segment's search visibility to its market performance, and Market Analysis is now out of beta and available to everyone."
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.seeTheKeywordsDrivingAnyMarket.items.compareSovToMarketShare',
                        'Compare Share of Voice against Market Share for every keyword in a segment, with search volume and top movers'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.seeTheKeywordsDrivingAnyMarket.items.brandVisibilityLeaderboards',
                        'See SOV leaderboards and trends by brand, overall, organic, or sponsored'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.seeTheKeywordsDrivingAnyMarket.items.productVisibilityByAsin',
                        'Drill into Product Visibility to see which ASINs actually show up on the search page'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-30-ai-chat-is-now-enabled-for-all',
        date: '2026-07-30',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.aiChatIsNowEnabledForAll.title',
            'AI Chat is now enabled for all'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.aiChatIsNowEnabledForAll.description',
                    'Every Cobalt organization now has access to <aiChat>AI Chat</aiChat>.'
                ),
                links: {
                    aiChat: { href: '/ai/chat' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.aiChatIsNowEnabledForAll.items.askCobaltFromAnyPage',
                        'Open Ask Cobalt from any page, or work in the full chat page when you want more room'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.aiChatIsNowEnabledForAll.items.askInPlainLanguage',
                        'Ask about your segments, keywords, Share of Voice, Amazon Ads, and DSP performance in plain language'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.aiChatIsNowEnabledForAll.items.startFromASkill',
                        'Start from a guided Skill in the <strong>/</strong> menu, or just describe what you are trying to figure out'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-29-ask-the-mcp-about-your-own-amazon-data',
        date: '2026-07-29',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.askTheMcpAboutYourOwnAmazonData.title',
            'Ask the MCP about your own Amazon data'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.askTheMcpAboutYourOwnAmazonData.description',
                    'Three new MCP tools open up your connected Seller Central and Vendor Central data at daily grain, the same numbers behind your Cobalt dashboard widgets.'
                ),
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.askTheMcpAboutYourOwnAmazonData.items.sellerCentralSalesAndTraffic',
                        'Ask about 3P sales and traffic: Buy Box win rate, sessions without sales, organic vs ad attributed revenue, TACoS'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.askTheMcpAboutYourOwnAmazonData.items.vendorCentralSalesAndInventory',
                        'Ask about 1P sales and inventory: what Amazon is ordering, net PPM by brand, aged stock, out of stocks, and open PO quantities'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.askTheMcpAboutYourOwnAmazonData.items.contactYourCsm',
                        'To learn more about the MCP or get access, contact your CSM'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-29-export-any-market-analysis-chart',
        date: '2026-07-29',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.exportAnyMarketAnalysisChart.title',
            'Export any Market Analysis chart'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.exportAnyMarketAnalysisChart.description',
                    'Hover over a chart in <marketAnalysis>Market Analysis</marketAnalysis> and click the download icon to take it with you, as an image or as the underlying data.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.exportAnyMarketAnalysisChart.items.downloadChartAsPng',
                        'Download a clean PNG, ready for a deck, a QBR, or a client email'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.exportAnyMarketAnalysisChart.items.downloadChartDataAsCsv',
                        'Download the numbers behind the chart as a CSV that works in Excel and BI tools right away'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.exportAnyMarketAnalysisChart.items.previewDataBeforeExport',
                        'Preview the data in the Data tab before you export'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-28-rating-count-filter-now-reads-ratings',
        date: '2026-07-28',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.ratingCountFilterNowReadsRatings.title',
            'Rating Count filter now reads ratings'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.ratingCountFilterNowReadsRatings.description',
                    'The Rating Count filter in <marketAnalysis>Market Analysis</marketAnalysis> was filtering on review count. It now filters on rating count as intended.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.ratingCountFilterNowReadsRatings.items.savedSegmentsMayShift',
                        'If you saved a segment using Rating Count, the product set may shift now that the filter reads the correct field'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-22-improve-share-of-voice-skill',
        date: '2026-07-22',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.improveShareOfVoiceSkill.title',
            'Improve Share of Voice with a guided Skill'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.improveShareOfVoiceSkill.description',
                    "Cobalt's first in-app skill in <aiChat>AI Chat</aiChat> walks you through improving Share of Voice for a saved US Market Analysis segment, turning analysis into a guided action plan."
                ),
                links: {
                    aiChat: { href: '/ai/chat' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.improveShareOfVoiceSkill.items.launchFromMenu',
                        'Launch it from the + button in AI Chat, or type <strong>/</strong> to open the Skills menu'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.improveShareOfVoiceSkill.items.guidedRecommendations',
                        'Get guided recommendations to improve Share of Voice for a saved segment'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.improveShareOfVoiceSkill.items.requiresUsSegment',
                        'Requires a saved US Market Analysis segment (more marketplaces coming soon)'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-22-ask-cobalt-segment-context',
        date: '2026-07-22',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.askCobaltSegmentContext.title',
            "Ask Cobalt about the segment you're viewing"
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.askCobaltSegmentContext.description',
                    'Open Ask Cobalt on a saved <marketAnalysis>Market Analysis</marketAnalysis> segment and it already knows which one, so you can start asking questions without describing the segment first.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.askCobaltSegmentContext.items.startWithoutNaming',
                        'Start asking about a saved segment without naming it first'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.askCobaltSegmentContext.items.contextFollowsToMainChat',
                        'Keep the context attached when you continue the conversation on the main chat page'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.askCobaltSegmentContext.items.comingToMorePages',
                        "Context awareness is coming to more pages, so Ask Cobalt will understand what you're looking at across the app"
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-22-amazon-dsp-performance',
        date: '2026-07-22',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.askAboutAmazonDspPerformance.title',
            'Ask about Amazon DSP performance'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.askAboutAmazonDspPerformance.description',
                    'The Cobalt AI Chat and MCP now answer Amazon DSP questions, not just Sponsored Ads, so you can track DSP spend, sales, and ROAS by asking in plain language (requires a connected Amazon DSP account).'
                ),
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.askAboutAmazonDspPerformance.items.trackDspMetrics',
                        'Track DSP spend, sales, and ROAS by advertiser, order, line item, or creative'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.askAboutAmazonDspPerformance.items.deliveryAndPlacement',
                        "See which line items aren't delivering and where budget landed by site, placement, and supply source (Twitch, Fire TV, and more)"
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.askAboutAmazonDspPerformance.items.audienceSegments',
                        'Compare audience segments on cost-per-order and conversion rate, with trends over time'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.askAboutAmazonDspPerformance.items.contactCsm',
                        '<strong>To learn more about the MCP or get access, contact your CSM</strong>'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-21-subscribe-and-save-visibility',
        date: '2026-07-21',
        category: 'dataUpdate',
        title: t(
            'navigation:TopNav.WhatsNew.entries.subscribeAndSaveVisibility.title',
            'See which products offer Subscribe & Save'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.subscribeAndSaveVisibility.description',
                    'A new optional column in the Products table flags products that recently offered Subscribe & Save, so you can see subscription competition at a glance.'
                ),
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.subscribeAndSaveVisibility.items.addColumn',
                        'Add the Subscribe & Save column to the <marketAnalysis>Market Analysis</marketAnalysis> Products table'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.subscribeAndSaveVisibility.items.recentAvailability',
                        'See which products have recently had Subscribe & Save available'
                    ),
                ],
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
        ],
    },
    {
        id: '2026-07-17-use-ai-chat-from-any-page',
        date: '2026-07-17',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.useAiChatFromAnyPage.title',
            'Ask Cobalt from any page'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.useAiChatFromAnyPage.description',
                    'A floating <strong>Ask Cobalt</strong> button in the bottom-right corner opens a resizable chat panel from any page, so you can ask questions without leaving your work.'
                ),
                links: {},
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.useAiChatFromAnyPage.items.pastSessions',
                        'Browse and reopen your past chat sessions'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.useAiChatFromAnyPage.items.searchHistory',
                        'Search across your full chat history'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.useAiChatFromAnyPage.items.resizePanel',
                        'Resize the panel and use it comfortably on any screen size'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-17-run-ai-workflow-reports-in-more-marketplaces',
        date: '2026-07-17',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.runAiWorkflowReportsInMoreMarketplaces.title',
            'Run AI Workflow reports in more marketplaces'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.runAiWorkflowReportsInMoreMarketplaces.description',
                    '<aiAnalyst>AI Workflow</aiAnalyst> reports now run beyond the US, covering major EU marketplaces plus the UK and Japan, each in its local currency.'
                ),
                links: {
                    aiAnalyst: { href: '/ai/workflows' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.runAiWorkflowReportsInMoreMarketplaces.items.marketplaceCoverage',
                        'Generate workflow reports for US, UK, DE, FR, IT, ES, and JP'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.runAiWorkflowReportsInMoreMarketplaces.items.localCurrency',
                        "Each report uses the marketplace's default currency (USD, EUR, GBP, JPY, and so on)"
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-17-ask-about-your-segments-in-the-mcp',
        date: '2026-07-17',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.askAboutYourSegmentsInTheMcp.title',
            'Ask about your segments in the MCP'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.askAboutYourSegmentsInTheMcp.description',
                    'The Cobalt MCP now knows your saved <marketAnalysis>Market Analysis</marketAnalysis> segments, so you can ask about them in plain language and get answers that match Market Analysis.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.askAboutYourSegmentsInTheMcp.items.listSegments',
                        'Ask which segments to look at and get them listed back'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.askAboutYourSegmentsInTheMcp.items.scopeToSegment',
                        'Scope any MCP question to a specific saved segment by name'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.askAboutYourSegmentsInTheMcp.items.curatedSlice',
                        'Analyze a curated slice of the market instantly, without redefining it each time'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.askAboutYourSegmentsInTheMcp.items.contactCsm',
                        '<strong>To learn more about the MCP or get access, contact your CSM</strong>'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-17-analyze-your-amazon-ads-in-the-mcp',
        date: '2026-07-17',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.analyzeYourAmazonAdsInTheMcp.title',
            'Analyze your Amazon Ads in the MCP'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.analyzeYourAmazonAdsInTheMcp.description',
                    'Your connected Amazon Ads account is now queryable through the Cobalt MCP, from campaign detail down to portfolio pacing, all in plain language.'
                ),
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeYourAmazonAdsInTheMcp.items.performanceMetrics',
                        'Pull spend, ad sales, ACoS or ROAS, CPC, and CTR at campaign, ad group, product, or account level'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeYourAmazonAdsInTheMcp.items.portfolioPacing',
                        'Track portfolio-level spend, sales, ACoS, and budget pacing, trended over time'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeYourAmazonAdsInTheMcp.items.wastedSpend',
                        "Find keywords that spend but don't convert, and see what you bid on"
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeYourAmazonAdsInTheMcp.items.contactCsm',
                        '<strong>To learn more about the MCP or get access, contact your CSM</strong>'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-16-diagnose-buy-box-loss-in-the-mcp',
        date: '2026-07-16',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.diagnoseBuyBoxLossInTheMcp.title',
            'Diagnose buy box loss in the MCP'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.diagnoseBuyBoxLossInTheMcp.description',
                    'New MCP tools help you diagnose buy box loss and spot other sellers on your own ASINs, using your connected Seller or Vendor Central account.'
                ),
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.diagnoseBuyBoxLossInTheMcp.items.buyBoxWinner',
                        'See who is winning the buy box on any of your ASINs, and at what price'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.diagnoseBuyBoxLossInTheMcp.items.unauthorizedSellers',
                        'Identify new or unauthorized sellers on your listings, with feedback scores and fulfillment split'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.diagnoseBuyBoxLossInTheMcp.items.sellerDrilldown',
                        "Drill into one seller's price and buy box share across your ASINs over time"
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.diagnoseBuyBoxLossInTheMcp.items.contactCsm',
                        '<strong>To learn more about the MCP or get access, contact your CSM</strong>'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-07-see-seller-contact-details',
        date: '2026-07-07',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.seeSellerContactDetails.title',
            'See seller contact details in Market Analysis'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.seeSellerContactDetails.description',
                    'Get contact information for any seller without leaving <marketAnalysis>Market Analysis</marketAnalysis>, so you can quickly identify who is behind an account and act on it faster.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.seeSellerContactDetails.items.openDrawer',
                        'Click any seller in Market Analysis to open the slide-out drawer'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.seeSellerContactDetails.items.viewDetails',
                        'View available contact details for that seller in one place'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.seeSellerContactDetails.items.reachOut',
                        'Reach out to any seller you are researching, from competitors to potential partners'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-06-build-filters-with-your-voice',
        date: '2026-07-06',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.buildFiltersWithYourVoice.title',
            'Build filters with your voice in Market Analysis'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.buildFiltersWithYourVoice.description',
                    'Describe the segment you want out loud instead of typing. Voice input is now available for AI filters in <marketAnalysis>Market Analysis</marketAnalysis>.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.buildFiltersWithYourVoice.items.micButton',
                        'Click the mic button to describe a segment by voice'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.buildFiltersWithYourVoice.items.noTyping',
                        'Cobalt turns your description into an AI filter, no typing required'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-02-see-keyword-rankings-in-mcp',
        date: '2026-07-02',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.seeKeywordRankingsInMcp.title',
            'See keyword rankings in the MCP'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.seeKeywordRankingsInMcp.description',
                    'A new MCP tool shows where products rank on an Amazon search page, week by week, so you can track organic and paid placement over time.'
                ),
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.seeKeywordRankingsInMcp.items.organicLeaders',
                        'Ask who ranks at the top of organic results for any keyword'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.seeKeywordRankingsInMcp.items.asinTrend',
                        'Track how a specific ASIN ranks week by week for a keyword'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.seeKeywordRankingsInMcp.items.organicVsPaid',
                        'Compare organic versus sponsored placement for a search term'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.seeKeywordRankingsInMcp.items.contactCsm',
                        '<strong>To learn more about the MCP or get access, contact your CSM</strong>'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-07-02-run-jungle-scout-skills',
        date: '2026-07-02',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.runJungleScoutSkills.title',
            'Run Jungle Scout Skills in your AI assistant'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.runJungleScoutSkills.description',
                    'Skills are pre-built workflows for the Jungle Scout MCP that guide your AI assistant through proven approaches to common Amazon questions, like benchmarking a brand or finding emerging keywords.'
                ),
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.runJungleScoutSkills.items.workflows',
                        'Benchmark a brand against its category, or find emerging keywords in a category'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.runJungleScoutSkills.items.availability',
                        'Available on Claude via the jungle-scout-cobalt plugin and on ChatGPT'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.runJungleScoutSkills.items.contactCsm',
                        '<strong>To learn more about the MCP or get access, contact your CSM</strong>'
                    ),
                ],
            },
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.runJungleScoutSkills.setup',
                    'Browse the skills in the <repo>public repo</repo> and follow the <guide>MCP Getting Started guide</guide> to set up.'
                ),
                links: {
                    repo: {
                        href: 'https://github.com/Junglescout/ai-skills',
                    },
                    guide: {
                        href: 'https://app.notion.com/p/junglescout/Jungle-Scout-MCP-Getting-Started-guide-3742362b8e5b802288bec7a7f7941566?source=copy_link#3912362b8e5b805fb475e59b91af5bca',
                    },
                },
            },
        ],
    },
    {
        id: '2026-07-01-updated-dashboard-templates',
        date: '2026-07-01',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.updatedDashboardTemplates.title',
            'Updated dashboard templates on Market Analysis'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.updatedDashboardTemplates.description',
                    'Eleven <dashboards>dashboard templates</dashboards> have been rebuilt on our newer Market Analysis data and are live now, keeping your reporting current with the latest Cobalt experience.'
                ),
                links: {
                    dashboards: { href: '/dashboards?tab=allDashboards' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.updatedDashboardTemplates.items.coverage',
                        'Covers segment dynamics, product launch prep, BSR tracking, and market share vs share of voice'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.updatedDashboardTemplates.items.includes',
                        'Includes Tariff Insights, Enhanced Market Intelligence Reporting, and 1-page segment overview PDFs'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-06-30-ask-cobalt-with-ai-chat',
        date: '2026-06-30',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.askCobaltWithAiChat.title',
            'Ask Cobalt anything with AI Chat'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.askCobaltWithAiChat.description',
                    'Cobalt <aiChat>AI Chat</aiChat> is a new conversational assistant built into Cobalt. Ask questions in plain language and get answers grounded in your account data and Jungle Scout estimates.'
                ),
                links: {
                    aiChat: { href: '/ai/chat' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.askCobaltWithAiChat.items.plainLanguage',
                        'Ask about your business in plain language'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.askCobaltWithAiChat.items.groundedAnswers',
                        'Get answers from your actual account data and Jungle Scout sales estimates'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.askCobaltWithAiChat.items.followUps',
                        'Keep the conversation going with follow-ups using saved session history'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-06-19-fiscal-year-date-presets',
        date: '2026-06-19',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.fiscalYearDatePresets.title',
            'Report on your fiscal year'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.fiscalYearDatePresets.description',
                    "Configure your organization's fiscal year and use new presets to align Cobalt reporting with your accounting calendar."
                ),
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.fiscalYearDatePresets.items.setFiscalYear',
                        "Set your organization's custom fiscal year in <settings>Settings</settings>"
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.fiscalYearDatePresets.items.newPresets',
                        'Use the new Last Fiscal Year and Fiscal Year to Date presets in date pickers across Cobalt'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.fiscalYearDatePresets.items.comingSoon',
                        'Coming soon to Category Performance, Executive Homepage, and Market Analysis'
                    ),
                ],
                links: {
                    settings: { href: '/settings/organization' },
                },
            },
        ],
    },
    {
        id: '2026-06-18-analyze-products-at-parent-and-variant-level',
        date: '2026-06-18',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.analyzeProductsAtParentAndVariantLevel.title',
            'Analyze products at the parent and variant level'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.analyzeProductsAtParentAndVariantLevel.description',
                    'See how a parent and its variants perform together in <marketAnalysis>Market Analysis</marketAnalysis>, with revenue rolled up at the parent level and a deeper view of how each variant contributes.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeProductsAtParentAndVariantLevel.items.toggleByParent',
                        'Toggle the Products table between All Products and By Parent to see metrics rolled up across variants'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeProductsAtParentAndVariantLevel.items.compareVariants',
                        "Open the parent drawer to compare all of a parent's variants in one place"
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.analyzeProductsAtParentAndVariantLevel.items.drillIntoVariant',
                        'Drill into any variant to see its performance in the context of its parent, including when it switched parents over time'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-06-17-introducing-jungle-scout-mcp',
        date: '2026-06-17',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.introducingJungleScoutMcp.title',
            'Introducing the Jungle Scout MCP: Cobalt data, now in your AI assistant'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.introducingJungleScoutMcp.description',
                    'The new Jungle Scout MCP connects Claude, ChatGPT, or Gemini directly to our Amazon data. Ask questions in plain language. Explore, analyze, and report exactly as you want it.'
                ),
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.introducingJungleScoutMcp.items.amazonUseCases',
                        'Run category analysis, competitive benchmarking, pricing optimization, keyword research, and Share of Voice tracking'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.introducingJungleScoutMcp.items.contactCsm',
                        'Contact your CSM to set up MCP access for your organization'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-06-16-take-down-unauthorized-sellers-with-sigil',
        date: '2026-06-16',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.takeDownUnauthorizedSellersWithSigil.title',
            'Take down unauthorized sellers with Sigil'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.takeDownUnauthorizedSellersWithSigil.description',
                    "Cobalt shows you the revenue leak, Sigil shuts it down. Hand off unauthorized sellers from <sellerCompliance>Seller Compliance</sellerCompliance> straight to Sigil's enforcement team."
                ),
                links: {
                    sellerCompliance: { href: '/distribution-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.takeDownUnauthorizedSellersWithSigil.items.startEnforcementAnywhere',
                        "Start enforcement wherever you spot a problem, whether you're scanning the seller list, drilling into a single seller, or investigating Buy Box issues"
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.takeDownUnauthorizedSellersWithSigil.items.sigilHandlesEnforcement',
                        "Sigil's team investigates each seller, builds a case, and follows through until they're off your listings"
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-06-16-generate-ai-reports-for-any-segment',
        date: '2026-06-16',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.generateAiReportsForAnySegment.title',
            'Generate AI reports for any Market Analysis Segment'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.generateAiReportsForAnySegment.description',
                    'A new Segment Analysis report is available in <aiAnalyst>AI Analyst</aiAnalyst>. Turn Market Analysis data into a written analysis in a few clicks.'
                ),
                links: {
                    aiAnalyst: { href: '/workflows' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.generateAiReportsForAnySegment.items.runFromAnywhere',
                        'Run the report from AI Analyst or directly from a Market Analysis Segment'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.generateAiReportsForAnySegment.items.chooseTimeframe',
                        'Pick a timeframe of last 4 weeks, last 12 weeks, or last year'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.generateAiReportsForAnySegment.items.focusBrand',
                        'Optionally choose a focus brand to highlight in the analysis'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-06-16-cleaner-excel-exports-from-market-analysis',
        date: '2026-06-16',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.cleanerExcelExportsFromMarketAnalysis.title',
            'Cleaner Excel exports from Market Analysis'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.cleanerExcelExportsFromMarketAnalysis.description',
                    "Exports from <marketAnalysis>Market Analysis</marketAnalysis> now write each column with its proper Excel value type, so sorting, filtering, and formatting work the way you'd expect."
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.cleanerExcelExportsFromMarketAnalysis.items.properColumnTypes',
                        'Currency, number, percent, boolean, and text columns are correctly typed in the file'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.cleanerExcelExportsFromMarketAnalysis.items.cleanerSortingAndFormatting',
                        'Sorting and Excel formatting work without manual cleanup'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-06-15-sort-and-export-products-from-any-market-analysis-drawer',
        date: '2026-06-15',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.sortAndExportProductsFromAnyMarketAnalysisDrawer.title',
            'Sort and export products from any Market Analysis drawer'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.sortAndExportProductsFromAnyMarketAnalysisDrawer.description',
                    'Drawers in <marketAnalysis>Market Analysis</marketAnalysis> now let you sort and export product lists, so pulling a quick list for a brand, category, seller, or price band is one click away.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.sortAndExportProductsFromAnyMarketAnalysisDrawer.items.sortByRevenueOrUnits',
                        'Sort drawer product lists by revenue or units'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.sortAndExportProductsFromAnyMarketAnalysisDrawer.items.exportFromDrawers',
                        'Export products from a brand, category, seller, or pricing bucket drawer'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-06-10-track-map-violations-across-your-catalog',
        date: '2026-06-10',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.trackMapViolationsAcrossYourCatalog.title',
            'Track MAP violations across your catalog'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.trackMapViolationsAcrossYourCatalog.description',
                    'Set Minimum Advertised Price for any product and instantly see where offers are dipping below it, so MAP enforcement no longer lives in spreadsheets.'
                ),
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.trackMapViolationsAcrossYourCatalog.items.setMapInlineOrBulk',
                        'Edit MAP and Currency inline in Product Catalog, or upload a CSV to set MAP in bulk'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.trackMapViolationsAcrossYourCatalog.items.mapViolationsInRiskFactors',
                        'See MAP Violations as a new Risk Factor in Distribution Analysis, with both a period total and a Recent snapshot'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.trackMapViolationsAcrossYourCatalog.items.mapReferenceLineOnChart',
                        'A MAP reference line now appears on the Offers Over Time chart in Buy Box Performance'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-06-09-spot-risk-factors-at-a-glance-in-seller-compliance',
        date: '2026-06-09',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.spotRiskFactorsAtAGlanceInSellerCompliance.title',
            'Spot risk factors at a glance in Seller Compliance'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.spotRiskFactorsAtAGlanceInSellerCompliance.description',
                    '<sellerCompliance>Seller Compliance</sellerCompliance> now shows the latest complete counts for unauthorized sellers and products with a suppressed Buy Box, so you can size up risk without drilling in.'
                ),
                links: {
                    sellerCompliance: { href: '/distribution-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.spotRiskFactorsAtAGlanceInSellerCompliance.items.unauthorizedSellerCount',
                        'See unauthorized seller counts from the most recent complete day'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.spotRiskFactorsAtAGlanceInSellerCompliance.items.suppressedBuyBoxCount',
                        'See product counts with a suppressed Buy Box from the same window'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.spotRiskFactorsAtAGlanceInSellerCompliance.items.drillIntoDetails',
                        'Click either metric to view the specific sellers or products'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-06-09-filter-market-analysis-by-units-sold',
        date: '2026-06-09',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.filterMarketAnalysisByUnitsSold.title',
            'Filter Market Analysis by units sold'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.filterMarketAnalysisByUnitsSold.description',
                    'Narrow any <marketAnalysis>Market Analysis</marketAnalysis> segment by units sold, alongside the existing revenue and price filters.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.filterMarketAnalysisByUnitsSold.items.unitsSoldFilter',
                        'Filter products in any Market Analysis segment by units sold'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.filterMarketAnalysisByUnitsSold.items.aiFilterSupport',
                        'Works with the AI filter, so you can describe what you want in plain language'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-06-05-see-whos-selling-on-amazon',
        date: '2026-06-05',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.seeWhosSellingOnAmazon.title',
            "See who's selling on Amazon"
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.seeWhosSellingOnAmazon.description',
                    'The new Sellers tab in <marketAnalysis>Market Analysis</marketAnalysis> breaks any segment down by the sellers actually competing in it — not just the brands or products.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.seeWhosSellingOnAmazon.items.fulfillmentMix',
                        'View 1P vs 3P split and FBA vs FBM fulfillment mix'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.seeWhosSellingOnAmazon.items.sellersByCountry',
                        'See where competition is coming from, with sellers by country defaulting to the share based in China'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.seeWhosSellingOnAmazon.items.newAndRisingSellers',
                        'Spot new and rising sellers entering the market'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-06-05-faster-insights-market-analysis-tabs',
        date: '2026-06-05',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.fasterInsightsMarketAnalysisTabs.title',
            'Faster insights at the top of every Market Analysis tab'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.fasterInsightsMarketAnalysisTabs.description',
                    "Each <marketAnalysis>Market Analysis</marketAnalysis> tab now leads with the headline numbers, what's moving, and quick filter shortcuts — so you can get to the answer without scrolling."
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.fasterInsightsMarketAnalysisTabs.items.kpiCards',
                        "KPI cards show each tab's headline metrics and trends vs the prior period"
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.fasterInsightsMarketAnalysisTabs.items.moversCard',
                        'Movers card surfaces the biggest gainers and decliners, toggleable by revenue, units, or market share'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.fasterInsightsMarketAnalysisTabs.items.smartFilterChips',
                        'Smart filter chips give one-tap presets above each table'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-06-04-export-ai-analyst-reports-to-pdf',
        date: '2026-06-04',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.exportAiAnalystReportsToPdf.title',
            'Export AI Analyst reports to PDF'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.exportAiAnalystReportsToPdf.description',
                    'Save <aiAnalyst>AI Analyst</aiAnalyst> reports as PDFs to share with your team or include in client deliverables.'
                ),
                links: {
                    aiAnalyst: { href: '/workflows' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.exportAiAnalystReportsToPdf.items.exportToPdf',
                        'Export any AI Analyst report straight to PDF'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.exportAiAnalystReportsToPdf.items.findExportsInCsvDownloads',
                        'Find your exports in CSV Downloads in the top nav'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-06-03-see-1p-3p-breakdown-across-market-analysis',
        date: '2026-06-03',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.see1p3pBreakdownAcrossMarketAnalysis.title',
            'See 1P/3P breakdown across Market Analysis'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.see1p3pBreakdownAcrossMarketAnalysis.description',
                    'The 1P/3P over-time chart is now available inside the product, brand, and category drawers in <marketAnalysis>Market Analysis</marketAnalysis>, and the breakdown appears in the brand and category tables, not just products.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.see1p3pBreakdownAcrossMarketAnalysis.items.trendsInAllDrawers',
                        'View 1P/3P trends in any drawer (product, brand, or category)'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.see1p3pBreakdownAcrossMarketAnalysis.items.breakdownInTables',
                        '1P/3P column now visible in brand and category tables'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-06-01-filter-by-seller-using-natural-language',
        date: '2026-06-01',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.filterBySellerUsingNaturalLanguage.title',
            'Filter by seller using natural language'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.filterBySellerUsingNaturalLanguage.description',
                    'The AI Filter in <marketAnalysis>Market Analysis</marketAnalysis> now supports seller filters, so you can include or exclude specific sellers in plain English.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.filterBySellerUsingNaturalLanguage.items.naturalLanguagePrompts',
                        'Ask things like "fastest growing sellers in Coffee Makers"'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.filterBySellerUsingNaturalLanguage.items.combineCriteria',
                        'Combine seller filters with brand, revenue, and growth criteria'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.filterBySellerUsingNaturalLanguage.items.includeExcludeByName',
                        'Include or exclude specific sellers by name'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-05-27-sponsored-brands-fixed-price-campaigns-now-in-cobalt',
        date: '2026-05-27',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.sponsoredBrandsFixedPriceCampaignsNowInCobalt.title',
            'Sponsored Brands fixed-price campaigns now in Cobalt'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.sponsoredBrandsFixedPriceCampaignsNowInCobalt.description',
                    "Cobalt's <campaigns>campaign view</campaigns> now includes Amazon's flat-fee SB campaigns."
                ),
                links: {
                    campaigns: { href: '/campaigns' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.sponsoredBrandsFixedPriceCampaignsNowInCobalt.items.dailyCostFromBudget',
                        'Daily cost is calculated from the campaign budget, spread evenly across scheduled days'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.sponsoredBrandsFixedPriceCampaignsNowInCobalt.items.metricsWorkAsExpected',
                        'ACOS, ROAS, and other metrics work as expected'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-05-26-search-by-asin-in-product-catalog',
        date: '2026-05-26',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.searchByAsinInProductCatalog.title',
            'Search by ASIN in Product Catalog'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.searchByAsinInProductCatalog.description',
                    'The <productCatalog>Product Catalog</productCatalog> search field now accepts ASINs in addition to product titles, so you can jump straight to a product from any list, export, or report.'
                ),
                links: {
                    productCatalog: { href: '/product-catalog' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.searchByAsinInProductCatalog.items.pasteAsin',
                        'Paste an ASIN directly into the main search input'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.searchByAsinInProductCatalog.items.fasterLookups',
                        'Faster lookups when working from Amazon, exports, or reports'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-05-21-find-categories-faster-with-smarter-search',
        date: '2026-05-21',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.findCategoriesFasterWithSmarterSearch.title',
            'Find categories faster with smarter search'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.findCategoriesFasterWithSmarterSearch.description',
                    'Category search in <marketAnalysis>Market Analysis</marketAnalysis> now understands what you mean, not just what you type, so you can find the right category without knowing its exact name.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.findCategoriesFasterWithSmarterSearch.items.semanticSearch',
                        'AI-powered semantic search interprets meaning and intent'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.findCategoriesFasterWithSmarterSearch.items.keywordMatching',
                        'Improved keyword matching surfaces closer results'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.findCategoriesFasterWithSmarterSearch.items.fasterPerformance',
                        'Faster search performance'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-05-20-include-specific-asins-in-market-analysis',
        date: '2026-05-20',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.includeSpecificAsinsInMarketAnalysis.title',
            'Include specific ASINs in Market Analysis'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.includeSpecificAsinsInMarketAnalysis.description',
                    "You can now lock specific ASINs into any <marketAnalysis>Market Analysis</marketAnalysis> segment so they're always included — even when other filters would normally exclude them."
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.includeSpecificAsinsInMarketAnalysis.items.alwaysIncludedAsins',
                        'Add a list of ASINs that stay visible in your segment no matter what filters you apply'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.includeSpecificAsinsInMarketAnalysis.items.keepKeyProductsInView',
                        'Keep key competitors or your own products in view while exploring different filter combinations'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.includeSpecificAsinsInMarketAnalysis.items.worksWithExistingFilters',
                        'Works alongside all existing segment filters without overriding them'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-05-15-ai-filters',
        date: '2026-05-15',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.aiFilters.title',
            'Build filters faster with AI'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.aiFilters.description',
                    'Setting up filters in <marketAnalysis>Market Analysis</marketAnalysis> just got easier — describe what you want to see, and AI does the rest.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.aiFilters.items.promptControl',
                        'Ask in plain English instead of clicking through filter menus.'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.aiFilters.items.fasterExploration',
                        'Go from question to insight in seconds across products, keywords, and performance trends.'
                    ),
                ],
            },
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.aiFilters.closing',
                    'Perfect for when you know the question but not the exact filter setup.'
                ),
            },
        ],
    },
    {
        id: '2026-05-14-product-catalog-categories',
        date: '2026-05-14',
        availability: { all: ['sellingPartnerIntegration'] },
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.productCatalogCategories.title',
            'Explore Product Catalog by category'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.productCatalogCategories.description',
                    'See your <productCatalog>Product Catalog</productCatalog> through a category lens — new Parent Category and Category fields make it easy to spot performance trends across your hierarchy.'
                ),
                links: {
                    productCatalog: { href: '/product-catalog' },
                },
            },
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.productCatalogCategories.details',
                    'Filter and sort by category right in the table, and the category picker only shows the ones where you have products.'
                ),
            },
        ],
    },
    {
        id: '2026-05-14-product-group-by-sc-vc',
        date: '2026-05-14',
        availability: {
            all: ['sellingPartnerIntegration'],
            any: ['sellerCentral', 'vendorCentral'],
        },
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.productGroupByScVc.title',
            'Group Seller Central and Vendor Central data by product'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.productGroupByScVc.description',
                    'Group Sales and Traffic data by the new "Product (Image + Title + ASIN)" option in your <dashboards>Custom Dashboards</dashboards> to see how each product is performing across Seller Central and Vendor Central.'
                ),
                links: {
                    dashboards: { href: '/dashboards' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.productGroupByScVc.items.productContext',
                        "One column shows each product's image, title, and ASIN together — easy to scan and identify."
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.productGroupByScVc.items.dashboardAnalysis',
                        'Spot product-level wins and trends at a glance in your dashboards.'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-05-13-market-share-units',
        date: '2026-05-13',
        availability: { all: ['newSegmentExperience'] },
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.marketShareUnits.title',
            'View Market Share by revenue or units'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.marketShareUnits.description',
                    'In <marketAnalysis>Market Analysis</marketAnalysis>, you can now toggle Market Share between Revenue and Units to see your position the way that matters most.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.marketShareUnits.details',
                    'Use Revenue when sales value matters; switch to Units when volume tells the better story.'
                ),
            },
        ],
    },
    {
        id: '2026-05-12-homepage-date-ranges',
        date: '2026-05-12',
        availability: { all: ['executiveHomepage'] },
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.homepageDateRanges.title',
            'New Executive Homepage date ranges'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.homepageDateRanges.description',
                    'Get a wider view of your business on the <executiveHomepage>Executive Homepage</executiveHomepage> — new preset date ranges let you zoom out without touching the date picker.'
                ),
                links: {
                    executiveHomepage: { href: '/' },
                },
            },
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.homepageDateRanges.details',
                    'Jump straight to Last Month, Last Quarter, or Last 12 Months with a single click.'
                ),
            },
        ],
    },
    {
        id: '2026-05-11-market-analysis-seller-filter',
        date: '2026-05-11',
        availability: { all: ['newSegmentExperience'] },
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.marketAnalysisSellerFilter.title',
            'Filter Market Analysis by seller'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.marketAnalysisSellerFilter.description',
                    'Zero in on the competition that matters — you can now filter <marketAnalysis>Market Analysis</marketAnalysis> by seller.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.marketAnalysisSellerFilter.details',
                    'Include just the sellers you want to track, or exclude the ones that are getting in the way of a clean comparison.'
                ),
            },
        ],
    },
    {
        id: '2026-05-11-market-analysis-product-columns',
        date: '2026-05-11',
        availability: { all: ['newSegmentExperience'] },
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.marketAnalysisProductColumns.title',
            'More product columns in Market Analysis'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.marketAnalysisProductColumns.description',
                    'Dig deeper into product research — the All Products table in <marketAnalysis>Market Analysis</marketAnalysis> now offers more columns to choose from.'
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.marketAnalysisProductColumns.items.priceAndAvailability',
                        'Layer in pricing details like average listing price, shipping price, country of origin, and first date available.'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.marketAnalysisProductColumns.items.identifiers',
                        'Find products by identifier — EAN, GTIN, UPC, or manufacturer part number.'
                    ),
                ],
            },
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.marketAnalysisProductColumns.closing',
                    'Every column is optional, so you can keep your view focused on what matters most.'
                ),
            },
        ],
    },
    {
        id: '2026-05-06-estimated-revenue-offers',
        date: '2026-05-06',
        availability: { any: ['buyBoxPerformance', 'sellerCompliance'] },
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.estimatedRevenueOffers.title',
            'See Revenue Opportunity in seller and offer views'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.estimatedRevenueOffers.description',
                    'Revenue Opportunity — the revenue tied to Buy Box ownership — is now visible while you investigate sellers and competing offers in <sellerCompliance>Seller Compliance</sellerCompliance>.'
                ),
                links: {
                    sellerCompliance: { href: '/distribution-analysis' },
                },
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.estimatedRevenueOffers.items.sellerDrawer',
                        "Check Revenue Opportunity in the Seller Drawer to see each seller's overlap with your catalog."
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.estimatedRevenueOffers.items.buyBoxOffers',
                        'See it in the Product Detail Buy Box Performance offers table while comparing competing offers.'
                    ),
                ],
            },
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.estimatedRevenueOffers.closing',
                    "For your own offers it shows actual revenue; for other sellers, it's the revenue at stake while they held the Buy Box."
                ),
            },
        ],
    },
    {
        id: '2026-05-05-market-analysis-brand-exclude',
        date: '2026-05-05',
        availability: { all: ['newSegmentExperience'] },
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.marketAnalysisBrandExclude.title',
            'Exclude brands in Market Analysis'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.marketAnalysisBrandExclude.description',
                    'The Brands filter in <marketAnalysis>Market Analysis</marketAnalysis> now supports excluding brands from a segment.'
                ),
                links: {
                    marketAnalysis: {
                        href: 'https://jsc.junglescout.com/market-analysis',
                    },
                },
            },
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.marketAnalysisBrandExclude.details',
                    'Remove brands that are not relevant to your analysis to create cleaner competitive comparisons.'
                ),
            },
        ],
    },
    {
        id: '2026-04-30-market-analysis-bsr-timeseries',
        date: '2026-04-30',
        availability: { all: ['newSegmentExperience'] },
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.marketAnalysisBsrTimeseries.title',
            'Track Best Seller Rank over time'
        ),
        content: [
            {
                type: 'richText',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.marketAnalysisBsrTimeseries.description',
                    "See how a product's Best Seller Rank moves over time — BSR is now a timeseries metric in <marketAnalysis>Market Analysis</marketAnalysis> product detail drawers."
                ),
                links: {
                    marketAnalysis: { href: '/market-analysis' },
                },
            },
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.marketAnalysisBsrTimeseries.details',
                    'Watch competitive position shift alongside your other product metrics, so you can spot trends before they show up in sales.'
                ),
            },
        ],
    },
    {
        id: '2026-04-13-expanded-timeseries-local-filters',
        date: '2026-04-13',
        availability: { any: ['executiveHomepage', 'sellerCompliance'] },
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.expandedTimeseriesLocalFilters.title',
            'Filter expanded timeseries charts independently'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.expandedTimeseriesLocalFilters.description',
                    'Expanded timeseries charts now support local filters on the Homepage and Seller Compliance page.'
                ),
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.expandedTimeseriesLocalFilters.items.startsFromPage',
                        'Start with the filters already applied on the page.'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.expandedTimeseriesLocalFilters.items.independentExploration',
                        'Change filters inside the expanded chart without changing the main page filters.'
                    ),
                ],
            },
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.expandedTimeseriesLocalFilters.closing',
                    'This keeps page-level filters stable while you inspect a specific chart in more detail.'
                ),
            },
        ],
    },
    {
        id: '2026-04-09-market-analysis-side-panel-metrics',
        date: '2026-04-09',
        availability: { all: ['newSegmentExperience'] },
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.marketAnalysisSidePanelMetrics.title',
            'Switch metrics in Market Analysis side panels'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.marketAnalysisSidePanelMetrics.description',
                    'Market Analysis side panel charts now let you switch between different metrics.'
                ),
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.marketAnalysisSidePanelMetrics.items.multiMetric',
                        'Compare trends across revenue, market share, units, price, and other key metrics.'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.marketAnalysisSidePanelMetrics.items.drawerCoverage',
                        'Use metric switching in category, brand, product, price band, and attribute drawers.'
                    ),
                ],
            },
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.marketAnalysisSidePanelMetrics.closing',
                    'You can stay in the side panel and change the analytical lens without reopening the drawer.'
                ),
            },
        ],
    },
    {
        id: '2026-04-08-sc-vc-brand-filter',
        date: '2026-04-08',
        availability: {
            all: ['sellingPartnerIntegration'],
            any: ['sellerCentral', 'vendorCentral'],
        },
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.scVcBrandFilter.title',
            'Filter SC and VC dashboard data by brand'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.scVcBrandFilter.description',
                    'Brand filtering is now available across Seller Central and Vendor Central dashboard data sources.'
                ),
            },
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.scVcBrandFilter.details',
                    "The filter shows your organization's own brands and works at both widget level and tab level."
                ),
            },
        ],
    },
    {
        id: '2026-03-02-walmart-data-ingestion-stopped',
        date: '2026-03-02',
        category: 'dataUpdate',
        title: t(
            'navigation:TopNav.WhatsNew.entries.walmartDataIngestionStopped.title',
            'Walmart data ingestion has stopped'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.walmartDataIngestionStopped.description',
                    'Walmart data ingestion stopped as of February 28, and Walmart-specific setup and write workflows have been removed or stopped.'
                ),
            },
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.walmartDataIngestionStopped.details',
                    'This includes the add Walmart integration button, Walmart dashboard templates, write operations, Walmart-related Rulebooks, and Walmart-related Alerts.'
                ),
            },
        ],
    },
    {
        id: '2026-02-10-resource-center',
        date: '2026-02-10',
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.resourceCenter.title',
            'Get contextual help without leaving Cobalt'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.resourceCenter.description',
                    'Get Help now opens an in-app Resource Center with page-aware support content.'
                ),
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.resourceCenter.items.pageAware',
                        'See help articles that are relevant to the page you are viewing.'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.resourceCenter.items.quickLinks',
                        'Access release notes, help articles, video guides, and the Metrics Glossary from one panel.'
                    ),
                ],
            },
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.resourceCenter.closing',
                    'The panel keeps help content available without taking you away from your current workflow.'
                ),
            },
        ],
    },
    {
        id: '2026-02-09-category-performance-hierarchy-ux',
        date: '2026-02-09',
        category: 'improvement',
        title: t(
            'navigation:TopNav.WhatsNew.entries.categoryPerformanceHierarchyUx.title',
            'Improved Category Performance hierarchy controls'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.categoryPerformanceHierarchyUx.description',
                    'Category Performance now has a cleaner expand and collapse experience for browsing category hierarchies.'
                ),
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.categoryPerformanceHierarchyUx.items.fullRowExpand',
                        'Expand or collapse categories by clicking the full row.'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.categoryPerformanceHierarchyUx.items.cleanerHierarchy',
                        'Use tree lines, indentation, leaf indicators, and truncation to scan long category names more easily.'
                    ),
                ],
            },
        ],
    },
    {
        id: '2026-01-27-buy-box-performance-one-year',
        date: '2026-01-27',
        availability: { all: ['buyBoxPerformance'] },
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.buyBoxPerformanceOneYear.title',
            'Analyze up to one year of Buy Box Performance'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.buyBoxPerformanceOneYear.description',
                    'The Buy Box Performance tab in Product Detail now supports longer historical date ranges.'
                ),
            },
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.buyBoxPerformanceOneYear.details',
                    'Choose Last 60 Days, Last 90 Days, Last 180 Days, or Last 365 Days; ranges longer than 60 days use weekly aggregated timeseries data.'
                ),
            },
        ],
    },
    {
        id: '2025-12-16-executive-homepage-ads-metrics',
        date: '2025-12-16',
        availability: { all: ['executiveHomepage'] },
        category: 'newFeature',
        title: t(
            'navigation:TopNav.WhatsNew.entries.executiveHomepageAdsMetrics.title',
            'Advertising metrics in Executive Homepage'
        ),
        content: [
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.executiveHomepageAdsMetrics.description',
                    'Executive Homepage now includes advertising performance metrics in the Performance and Key Metrics sections.'
                ),
            },
            {
                type: 'list',
                body: [
                    t(
                        'navigation:TopNav.WhatsNew.entries.executiveHomepageAdsMetrics.items.metrics',
                        'Review Ad Cost, Ad Sales, ACoS, RoAS, Ad Sales Contribution, Organic Sales, TRoAS, and TACoS.'
                    ),
                    t(
                        'navigation:TopNav.WhatsNew.entries.executiveHomepageAdsMetrics.items.glossary',
                        'Open metric definitions from the Customize Homepage Metrics modal.'
                    ),
                ],
            },
            {
                type: 'text',
                body: t(
                    'navigation:TopNav.WhatsNew.entries.executiveHomepageAdsMetrics.closing',
                    'This brings advertising and retail performance into the same homepage review flow.'
                ),
            },
        ],
    },
]
